import { all, call, put, takeEvery } from "redux-saga/effects";
import { common } from "src/types";
import { constants } from "src/constants/common";
import * as actions from "../slices/recordsSlice";
import records from "../../api/records";

// Helper to handle common API response validation
function validateResponse(response: common.ResponseGenerator) {
  if (response?.code === "-1") throw new Error("Session Expired");
  if (response?.code === "0" || response?.code === "2") throw response?.message;
}

function* getHealthRecordsSaga({
  payload: { resolve, reject, payload, queryParams },
}: {
  payload: {
    resolve: (res?: any) => void;
    reject: (err?: any) => void;
    payload: any;
    queryParams?: object;
  };
}) {
  try {
    const response: common.ResponseGenerator = yield call(
      records.getHealthRecords,
      payload,
      queryParams
    );

    validateResponse(response);

    const hasSearch = payload?.search?.length > 0;

    if (hasSearch) {
      yield put(actions.searchHealthRecordsSuccess(response));
    } else {
      yield put(actions.getHealthRecordsSuccess(response));
    }

    resolve(response);
  } catch (error) {
    const errMessage =
      error?.toString() || constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG;

    if (payload?.search) {
      yield put(actions.searchHealthRecordsError(errMessage));
    } else {
      yield put(actions.getHealthRecordsError(errMessage));
    }

    if (typeof reject === "function") {
      reject(error);
    }
  }
}

function* updateHealthRecordSaga({
  payload: { resolve, reject, payload },
}: {
  payload: {
    resolve: (res?: any) => void;
    reject: (err?: any) => void;
    payload: any;
  };
}) {
  try {
    const response: common.ResponseGenerator = yield call(
      records.updateHealthRecords,
      payload
    );

    validateResponse(response);

    yield put(actions.updateHealthRecordSuccess(response));
    resolve(response);
  } catch (error) {
    yield put(
      actions.updateHealthRecordError(
        error?.toString() || constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG
      )
    );

    if (typeof reject === "function") {
      reject(error);
    }
  }
}

function* getTestTypesSaga({
  payload: { resolve, reject, payload },
}: {
  payload: {
    resolve: (res?: any) => void;
    reject: (err?: any) => void;
    payload: any;
  };
}) {
  try {
    const response: common.ResponseGenerator = yield call(
      records.getTestTypes,
      payload
    );

    validateResponse(response);

    yield put(actions.getTestTypesSuccess(response));
    resolve(response);
  } catch (error) {
    yield put(
      actions.getTestTypesError(
        error?.toString() || constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG
      )
    );

    if (typeof reject === "function") {
      reject(error);
    }
  }
}

function* deleteRecordSaga({
  payload: { resolve, reject, payload },
}: {
  payload: {
    resolve: (res?: any) => void;
    reject: (err?: any) => void;
    payload: any;
  };
}) {
  try {
    const response: common.ResponseGenerator = yield call(
      records.deleteRecord,
      payload
    );

    validateResponse(response);

    yield put(actions.deleteRecordSuccess(response));
    resolve(response);
  } catch (error) {
    yield put(
      actions.deleteRecordError(
        error instanceof Error
          ? error.message
          : constants.ERROR_MESSAGES.SOMETHING_WENT_WRONG
      )
    );

    if (typeof reject === "function") {
      reject(error);
    }
  }
}

export default function* recordsSaga(): Generator {
  yield all([
    takeEvery(actions.getHealthRecordsRequest, getHealthRecordsSaga),
    takeEvery(actions.searchHealthRecordsRequest, getHealthRecordsSaga),
    takeEvery(actions.updateHealthRecordRequest, updateHealthRecordSaga),
    takeEvery(actions.getTestTypesRequest, getTestTypesSaga),
    takeEvery(actions.deleteRecordRequest, deleteRecordSaga),
  ]);
}
